'use client';

import { usePathname } from 'next/navigation';
import React, { FC, useEffect } from 'react';

import { Box, ScrollArea, Stack } from '@mantine/core';

import { AppSwitcherTabs } from 'components/header/components/AppSwitcher';
import { MenuCategoriesGrid } from 'components/header/components/Menu';
import { SidebarNavItem } from 'components/sidebar-nav/components/SidebarNav.Item';
import { SidebarNavSection } from 'components/sidebar-nav/components/SidebarNav.Section';
import { SidebarNavTitle } from 'components/sidebar-nav/components/SidebarNav.Title';
import { useNavItems } from 'hooks/use-nav-items';
import { useSidebarNav } from 'hooks/use-sidebar-nav';
import { sidebarNavState } from 'state/sidebar-nav';

import cx from './SidebarNav.module.scss';

export const SIDEBAR_NAV_WIDTH = 280;

const SidebarNav: FC & {
    Item: typeof SidebarNavItem;
    Title: typeof SidebarNavTitle;
    Section: typeof SidebarNavSection;
} = () => {
    const { isOpen } = useSidebarNav();

    const navItems = useNavItems();

    return (
        <>
            <Box className={cx.sidebar} data-sidebar-nav data-open={isOpen}>
                <ScrollArea className={cx.scrollArea} type="never" h="100dvh">
                    <Stack className={cx.inner}>
                        <AppSwitcherTabs />

                        {navItems.map((item) => (
                            <SidebarNav.Section key={item.title}>
                                <SidebarNav.Item icon={item.icon} href={item.href}>
                                    {item.title}
                                </SidebarNav.Item>

                                {item.items
                                    ?.filter((subItem) => !(subItem.href === item.href && subItem.title === item.title))
                                    .map((subItem) => (
                                        <SidebarNav.Section boxProps={{ ml: 47 }} key={subItem.title}>
                                            <SidebarNav.Item href={subItem.href}>{subItem.title}</SidebarNav.Item>

                                            <MenuCategoriesGrid
                                                categories={subItem.categories?.filter(
                                                    ({ hideOnMobile }) => !hideOnMobile,
                                                )}
                                            />
                                        </SidebarNav.Section>
                                    ))}
                            </SidebarNav.Section>
                        ))}
                    </Stack>
                </ScrollArea>
            </Box>
            <SidebarNavSetup />
        </>
    );
};

const SidebarNavSetup = () => {
    const { isOpen } = useSidebarNav();

    const pathname = usePathname();

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('is-sidebar-open');
        } else {
            document.body.classList.remove('is-sidebar-open');
        }
    }, [isOpen]);

    useEffect(() => {
        sidebarNavState.isOpen = false;
    }, [pathname]);

    useEffect(() => {
        const navbar = document.querySelector('#app_shell .mantine-AppShell-navbar');

        navbar?.addEventListener('click', (event) => {
            const clickInSidebar = (event.target as HTMLElement).closest('[data-sidebar-nav]');

            if (!clickInSidebar) {
                sidebarNavState.isOpen = false;
            }
        });
    }, []);

    return null;
};

SidebarNav.Item = SidebarNavItem;
SidebarNav.Title = SidebarNavTitle;
SidebarNav.Section = SidebarNavSection;

export { SidebarNav };
